import React, { useCallback, useId, useRef, useState } from 'react';
import logoGea from 'assets/images/logo-gea.png';
import { ROUTE_KEYS } from 'legacyfrontend/constants';
import type { FooterProps } from '../types';
import { sanitizeAboutHtml } from '../sanitizeHtml';
import { FooterModal } from '../FooterModal';
import { LEGAL_INFORMATION_TITLE, LegalInformationBody } from '../LegalInformationBody';
import { DefaultSocialIcon, SOCIAL_ICONS } from '../../header/icons';
import type { SocialNetwork } from '../../header/types';
import '../Footer.css';
import './FooterInstitucional.css';

const OTHER_LINKS_EMPTY_MESSAGE = 'Todavía no se agregaron enlaces.';
const OTHER_LINKS_TITLE = 'Otros enlaces';

const PAIS_ID_ARGENTINA = 1;
const PAIS_ID_PERU = 2;
const PAIS_ID_MEXICO = 4;

function safeHref(url?: string | null): string | null {
  if (!url) return null;
  const trimmed = url.trim();
  if (!trimmed) return null;
  if (/^(https?:|mailto:|tel:)/i.test(trimmed)) return trimmed;
  if (trimmed.startsWith('/') || trimmed.startsWith('#')) return trimmed;
  return null;
}

const PinIcon: React.FC = () => (
  <svg aria-hidden='true' viewBox='0 0 24 24'>
    <path d='M12 2a7 7 0 0 0-7 7c0 5.25 7 13 7 13s7-7.75 7-13a7 7 0 0 0-7-7Zm0 9.5A2.5 2.5 0 1 1 12 6.5a2.5 2.5 0 0 1 0 5Z' />
  </svg>
);

const ClockIcon: React.FC = () => (
  <svg aria-hidden='true' viewBox='0 0 24 24'>
    <path d='M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20Zm1 10.41 4.29 4.3-1.42 1.41L11 13.41V6h2Z' />
  </svg>
);

const FooterInstitucional: React.FC<FooterProps> = ({
  config,
  contact,
  officeInfo,
  onModalOpen,
  onModalClose,
}) => {
  const {
    backgroundColor = '#54a5e8',
    aboutUsHtml = '',
    aboutUsColor = '#273854',
    showPaymentMethods = false,
    showLegalConditions = false,
    showOtherLinks = false,
    showLegalInformation = false,
    showCancellationLink = false,
    otherLinks = [],
    footerLogos = [],
    address = '',
    hours = '',
    agencyLogoUrl = '',
  } = config;

  const { socialLinks = [] } = contact ?? {};

  const { razonSocial, legajo, cuit, paisId } = officeInfo ?? {};
  const sanitizedAbout = sanitizeAboutHtml(aboutUsHtml);
  const visibleSocialLinks = socialLinks.filter((s) => s.show && safeHref(s.url));
  const trimmedAddress = address.trim();
  const trimmedHours = hours.trim();

  const otherTitleId = useId();
  const legalTitleId = useId();
  const otherTriggerRef = useRef<HTMLButtonElement>(null);
  const legalTriggerRef = useRef<HTMLButtonElement>(null);
  const otherCloseRef = useRef<HTMLButtonElement>(null);
  const legalCloseRef = useRef<HTMLButtonElement>(null);
  const [otherOpen, setOtherOpen] = useState(false);
  const [legalOpen, setLegalOpen] = useState(false);

  const openOther = useCallback(() => {
    setOtherOpen(true);
    onModalOpen?.();
  }, [onModalOpen]);

  const closeOther = useCallback(() => {
    setOtherOpen(false);
    otherTriggerRef.current?.focus();
    onModalClose?.();
  }, [onModalClose]);

  const openLegal = useCallback(() => {
    setLegalOpen(true);
    onModalOpen?.();
  }, [onModalOpen]);

  const closeLegal = useCallback(() => {
    setLegalOpen(false);
    legalTriggerRef.current?.focus();
    onModalClose?.();
  }, [onModalClose]);

  const officeInfoLines =
    paisId === PAIS_ID_PERU
      ? [razonSocial, cuit && `RUC ${cuit}`]
      : paisId === PAIS_ID_MEXICO
        ? [razonSocial]
        : [razonSocial, legajo && `Legajo ${legajo}`, cuit && `CUIT ${cuit}`];
  const hasOfficeInfo = officeInfoLines.some(Boolean);
  const showArgentinaLegalInformation = showLegalInformation && paisId === PAIS_ID_ARGENTINA;

  return (
    <>
      <footer
        className='footer-institucional'
        style={{ '--brand': backgroundColor } as React.CSSProperties}
      >
        <div className='footer-institucional-inner'>
          <div className='footer-institucional-topbar'>
            <div className='footer-institucional-links'>
              {paisId === PAIS_ID_PERU ? (
                <>
                  {showLegalConditions && (
                    <a href={ROUTE_KEYS.TERMS_OF_USE} rel='noreferrer' target='_blank'>
                      Términos de uso
                    </a>
                  )}
                  {/* Libro de Reclamaciones is a legal requirement in Peru — never toggleable off. */}
                  <a href={ROUTE_KEYS.CLAIMS} rel='noreferrer' target='_blank'>
                    Libro de Reclamaciones
                  </a>
                  {showPaymentMethods && (
                    <a href={ROUTE_KEYS.PAYMENT_METHODS} rel='noreferrer' target='_blank'>
                      Formas de pagos
                    </a>
                  )}
                  {showLegalInformation && (
                    <a href={ROUTE_KEYS.PRIVACY_POLICY} rel='noreferrer' target='_blank'>
                      Política de privacidad
                    </a>
                  )}
                </>
              ) : paisId === PAIS_ID_MEXICO ? (
                <>
                  {showLegalInformation && (
                    <a href={ROUTE_KEYS.PRIVACY_POLICY_MX} rel='noreferrer' target='_blank'>
                      Aviso de privacidad
                    </a>
                  )}
                  {showLegalConditions && (
                    <a href={ROUTE_KEYS.GENERAL_CONDITIONS} rel='noreferrer' target='_blank'>
                      Términos
                    </a>
                  )}
                </>
              ) : (
                <>
                  {showPaymentMethods && (
                    <a href={ROUTE_KEYS.PAYMENT_METHODS} rel='noreferrer' target='_blank'>
                      Formas de pagos
                    </a>
                  )}
                  {showLegalConditions && (
                    <a href={ROUTE_KEYS.GENERAL_CONDITIONS} rel='noreferrer' target='_blank'>
                      Condiciones generales de Contratación
                    </a>
                  )}
                  {showOtherLinks && (
                    <button
                      aria-controls={otherTitleId}
                      className='footer-link--button'
                      onClick={openOther}
                      ref={otherTriggerRef}
                      type='button'
                    >
                      Otros Enlaces
                    </button>
                  )}
                  {showArgentinaLegalInformation && (
                    <button
                      aria-controls={legalTitleId}
                      onClick={openLegal}
                      ref={legalTriggerRef}
                      type='button'
                    >
                      {LEGAL_INFORMATION_TITLE}
                    </button>
                  )}
                  {showCancellationLink && (
                    <a href={ROUTE_KEYS.ARREPENTIMIENTO} rel='noreferrer' target='_blank'>
                      Botón de arrepentimiento
                    </a>
                  )}
                </>
              )}
            </div>
            {visibleSocialLinks.length > 0 ? (
              <div className='footer-institucional-socials'>
                {visibleSocialLinks.map((social, index) => {
                  const href = safeHref(social.url);
                  if (!href) return null;
                  const Icon =
                    SOCIAL_ICONS[social.network.toLowerCase() as SocialNetwork] ??
                    DefaultSocialIcon;
                  return (
                    <a
                      href={href}
                      key={`${social.network}-${index}`}
                      rel='noreferrer noopener'
                      target='_blank'
                    >
                      <Icon />
                    </a>
                  );
                })}
              </div>
            ) : null}
          </div>

          <div className='footer-institucional-brand'>
            {agencyLogoUrl ? (
              <img
                alt='Logo agencia'
                className='footer-institucional-agency-logo'
                src={agencyLogoUrl}
              />
            ) : null}
            {agencyLogoUrl ? (
              <span aria-hidden='true' className='footer-institucional-divider' />
            ) : null}
            <div className='footer-institucional-partners'>
              <img alt='GEA' className='footer-gea' src={logoGea} />
              {footerLogos.map((logo, index) => {
                const href = safeHref(logo.linkUrl);
                const img = (
                  <img alt={logo.name} className='footer-partner-logo' src={logo.imageUrl} />
                );
                return href ? (
                  <a
                    href={href}
                    key={`${logo.name}-${index}`}
                    rel='noreferrer noopener'
                    target='_blank'
                  >
                    {img}
                  </a>
                ) : (
                  <span key={`${logo.name}-${index}`}>{img}</span>
                );
              })}
            </div>
          </div>

          {sanitizedAbout.trim() ? (
            <section className='footer-institucional-about'>
              <h3>Sobre nosotros</h3>
              <div
                className='footer-institucional-about-text'
                dangerouslySetInnerHTML={{ __html: sanitizedAbout }}
                style={{ color: aboutUsColor }}
              />
            </section>
          ) : null}

          {hasOfficeInfo ? (
            <p className='footer-office footer-institucional-office'>
              {officeInfoLines.filter(Boolean).map((line, index, arr) => (
                <React.Fragment key={index}>
                  {line}
                  {index < arr.length - 1 ? <br /> : null}
                </React.Fragment>
              ))}
            </p>
          ) : null}

          {trimmedAddress || trimmedHours ? (
            <div className='footer-institucional-meta'>
              {trimmedAddress ? (
                <span className='footer-institucional-meta-item'>
                  <PinIcon />
                  <span>{trimmedAddress}</span>
                </span>
              ) : null}
              {trimmedHours ? (
                <span className='footer-institucional-meta-item'>
                  <ClockIcon />
                  <span>{trimmedHours}</span>
                </span>
              ) : null}
            </div>
          ) : null}
        </div>
      </footer>

      <FooterModal
        closeRef={otherCloseRef}
        labelledBy={otherTitleId}
        onClose={closeOther}
        open={otherOpen}
        title={OTHER_LINKS_TITLE}
      >
        {otherLinks.length === 0 ? (
          <p className='footer-modal-empty'>{OTHER_LINKS_EMPTY_MESSAGE}</p>
        ) : (
          <div className='footer-modal-links'>
            {otherLinks.map((link, index) => {
              const href = safeHref(link.url);
              if (!href) return null;
              return (
                <a
                  href={href}
                  key={`${link.text}-${index}`}
                  rel='noreferrer noopener'
                  target='_blank'
                >
                  <span>{link.text}</span>
                  <span aria-hidden='true'>↗</span>
                </a>
              );
            })}
          </div>
        )}
      </FooterModal>

      {legalOpen ? (
        <FooterModal
          closeRef={legalCloseRef}
          labelledBy={legalTitleId}
          onClose={closeLegal}
          open={legalOpen}
          title={LEGAL_INFORMATION_TITLE}
        >
          <LegalInformationBody paisId={paisId} />
        </FooterModal>
      ) : null}
    </>
  );
};

export default FooterInstitucional;
